import axios from './common';

const state = {
    scormCourses: [],
    scormCoursesQueryParams: {
        description: null,
        limit: 10,
        page: 1,
    },
    currentScormCourseItem: null,
    editableScormCourseItem: null,
    scormCoursesLoading: false,
    scormCourseItemLoading: false,
    scormCourseApiError: null,
    totalScormCourses: 0,
};

const getters = {
    scormCourses: state => {
        return state.scormCourses;
    },
    scormCoursesQueryParams: state => {
        return state.scormCoursesQueryParams;
    },
    scormCoursesLoading: state => {
        return state.scormCoursesLoading;
    },
    currentScormCourseItem: state => {
        return state.currentScormCourseItem;
    },
    editableScormCourseItem: state => {
        return state.editableScormCourseItem;
    },
    scormCourseItemLoading: state => {
        return state.scormCourseItemLoading;
    },
    scormCourseApiError: state => {
        return state.scormCourseApiError;
    },
    totalScormCourses: state => {
        return state.totalScormCourses;
    },
};

const actions = {

    getScormCourses(context) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: 'Loading Scorm Packages',
        }, {root: true}).then(() => context.commit('setScormCoursesLoading', true));

        axios.get(`${process.env.VUE_APP_SERVICE_URI_SCORM}/api/courses/`, {
            params: context.state.scormCoursesQueryParams,
        })
            .then(response => {
                context.dispatch('addNotification', {
                    status: 'success',
                    message: 'Scorm Courses successfully loaded',
                }, {root: true}).then(function() {
                    context.commit('setScormCoursesLoading', false);
                    context.commit('setScormCourses', response.data);
                });
            });
    },
    async getScormCourse(context, item) {
        context.dispatch('addNotification', {
            status: 'loading',
            message: `Retrieving Scorm Course "${item.title}"`,
        }, {root: true}).then(function () {
            context.commit('setScormCourseItemLoading', true);
            context.commit('setScormCourseApiError', null);
        });

        try {
            const response = await axios.get(`${process.env.VUE_APP_SERVICE_URI_SCORM}/api/courses/${item._id}`);
            context.commit('setCurrentScormCourseItem', response.data);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Scorm Course "${item.originalName}" deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Retrieval failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    console.error('scormCourseApiError', error);
                });
            }
        } finally {
            context.commit('setScormCourseItemLoading', false);
        }
    },
    async addScormCourse(context, item) {

        if(! item || ! item.file) {

            context.dispatch('addNotification', {
                status: 'error',
                message: `Failed to add SCORM file, please try again`,
            }, {root: true}).then(() => {
                context.commit('setScormCourseApiError', {
                    fields: {
                        file: 'Please select',
                    },
                });
            });

            return;
        }

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Adding Scorm Package "${item.file.originalName}"`,
        }, {root: true}).then(function() {
            context.commit('setScormCoursesLoading', true);
            context.commit('setScormCourseApiError', null);
        });

        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('courseId', item.courseId);

        try {
            await axios.post(`${process.env.VUE_APP_SERVICE_URI_SCORM}/api/courses`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Scorm Package "${item.originalName}" successfully added`,
            }, {root: true});

            return true;

        } catch (error) {

            if (error.response) {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Failed to add Scorm Package "${item.originalName}", please try again`,
                }, {root: true}).then(() => {
                    context.commit('setScormCourseApiError', error.response.data);
                });
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Add failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    context.commit('setScormCourseApiError', error);
                    console.error('scormCourseApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setScormCourseItemLoading', false);
            await context.dispatch('getScormCourses');
        }
    },
    clearScormCourseApiError(context) {
        context.commit('setScormCourseApiError', null);
    },
    clearScormCourseItems(context) {
        context.commit('setCurrentScormCourseItem', null);
        context.commit('setEditableScormCourseItem', null);
    },
    async deleteScormCourse(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Deleting Scorm Course "${item.title}"`,
        }, {root: true}).then(() => context.commit('setScormCourseItemLoading', true));

        try {
            await axios.delete(`${process.env.VUE_APP_SERVICE_URI_SCORM}/api/courses/${item._id}`);
            await context.dispatch('addNotification', {
                status: 'success',
                message: `Successfully deleted Scorm Course "${item.originalName}"`,
            }, {root: true});
            return true;
        } catch(error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Scorm Course "${item.originalName}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Delete failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('setScormCourseApiError', error);
                    console.error('scormCourseApiError', error);
                });
            }
            return false;
        } finally {
            context.commit('setScormCourseItemLoading', false);
            await context.dispatch('clearScormCourseItems');
            await context.dispatch('getScormCourses');
        }
    },
    async editScormCourse(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Editing Scorm Course "${item.originalName}"`,
        }, {root: true}).then(function() {
            context.commit('setScormCourseItemLoading', true);
            context.commit('setScormCourseApiError', null);
        });

        const formData = new FormData();
        formData.append('file', item.file);

        try {
            await axios.put(`${process.env.VUE_APP_SERVICE_URI_SCORM}/api/courses/${item._id}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Scorm Package "${item.originalName}" successfully edited `,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Scorm Package "${item.originalName}" was edited and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Edit failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('setScormCourseApiError', error);
                    console.error('setScormCourseApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setScormCourseItemLoading', false);
            await context.dispatch('clearScormCourseItems');
            await context.dispatch('getScormCourses');
        }
    },
    setScormCoursesPaginationOptions(context, options) {
        context.commit('scormCoursesPaginationOptions', options);
    },
    setScormCoursesSearch(context, search) {
        context.commit('scormCoursesSearch', search);
    },
};

const mutations = {
    scormCoursesPaginationOptions(state, options) {
        state.scormCoursesQueryParams.limit = options.itemsPerPage ?? 10;
        state.scormCoursesQueryParams.page = options.page ?? 1;
    },
    scormCoursesSearch(state, search) {
        state.scormCoursesQueryParams.originalName = `/${search.trim()}/i`;
    },
    setScormCourses(state, responseData) {
        state.scormCourses = responseData.docs;
        state.totalScormCourses = responseData.totalDocs;
    },
    setCurrentScormCourseItem(state, item) {
        state.currentScormCourseItem = item;
        state.editableScormCourseItem = {...item};
    },
    setEditableScormCourseItem(state, item) {
        state.editableScormCourseItem = item;
    },
    setScormCoursesLoading(state, isLoading) {
        state.scormCoursesLoading = isLoading;
    },
    setScormCourseItemLoading(state, isLoading) {
        state.scormCourseItemLoading = isLoading;
    },
    setScormCourseApiError(state, error) {
        state.scormCourseApiError = error;
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
